import { FormEvent, useState } from 'react';

import {
  type ApiBatchUploadOrgPropertiesResponse,
  ORG_PROPERTIES_CSV_OPTIONAL_COLUMNS,
  ORG_PROPERTIES_CSV_REQUIRED_COLUMNS,
  type OrgPropertiesCsvColumn,
} from '@kaiizen/kai-pro-api';
import { useBatchUploadOrgProperties, useCurrentOrg } from '@kaiizen/kai-pro-api-react-state';

import { downloadBlob, formatCsv } from '@/Client/Lib';
import { CommonActionButton } from '@kaiizen/kai-shiki/react';

import { getErrorMessage } from './superAdminToolsLib';
import { DEFAULT_SUPER_ADMIN_TOOLS_PANEL_STYLES, SuperAdminToolsPanelStyles } from './superAdminToolsPanelStyles';

type BatchUploadRowResult = ApiBatchUploadOrgPropertiesResponse['results'][number];

const SAMPLE_ROW: Record<OrgPropertiesCsvColumn, string> = {
  orgId: '',
  address: '123 Main St, Atlanta, GA 30318',
  customId: 'CUSTOMER-00123',
  market: '',
  beds: '3',
  baths: '2.5',
  yearBuilt: '1990',
  squareFeet: '1850',
};

/** A sample upload: every column, and one example row for the given org. */
const getSampleCsv = (orgId: string) => {
  const columns = [...ORG_PROPERTIES_CSV_REQUIRED_COLUMNS, ...ORG_PROPERTIES_CSV_OPTIONAL_COLUMNS];
  const row = { ...SAMPLE_ROW, orgId };
  return formatCsv([columns, columns.map(column => row[column])]);
};

/** The failed rows as a CSV, numbered as spreadsheet rows (the header is row 1), so they are easy to find and fix. */
const getFailedRowsCsv = (results: BatchUploadRowResult[]) =>
  formatCsv([
    ['row', 'orgId', 'address', 'customId', 'market', 'error'],
    ...results.flatMap(rowResult =>
      rowResult.status === 'error'
        ? [[String(rowResult.rowIndex + 2), rowResult.orgId, rowResult.address, rowResult.customId, rowResult.market, rowResult.error ?? '']]
        : []
    ),
  ]);

const BatchUploadSummary = (
  { response: { summary, results }, styles }: { response: ApiBatchUploadOrgPropertiesResponse; styles: SuperAdminToolsPanelStyles },
) => (
  <>
    <div css={summary.error ? styles.error : styles.success}>{`${summary.ok} uploaded, ${summary.error} failed`}</div>
    {summary.error > 0 && (
      <div css={styles.actions}>
        <CommonActionButton onClick={() => downloadBlob(new Blob([getFailedRowsCsv(results)], { type: 'text/csv' }), 'batch-upload-failed-rows.csv')}>
          {'Download failed rows'}
        </CommonActionButton>
      </div>
    )}
  </>
);

/** Super admin upload of a CSV of properties, which creates each row's property in the org its row names. */
export const SuperAdminBatchUpload = ({ styles = DEFAULT_SUPER_ADMIN_TOOLS_PANEL_STYLES }: { styles?: SuperAdminToolsPanelStyles }) => {
  const [file, setFile] = useState<File>();
  const [response, setResponse] = useState<ApiBatchUploadOrgPropertiesResponse>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const batchUpload = useBatchUploadOrgProperties();
  const { currentOrg } = useCurrentOrg();

  const upload = async (event: FormEvent) => {
    event.preventDefault();
    setErrorMessage(undefined);
    setResponse(undefined);
    try {
      setResponse(await batchUpload.mutate(file!));
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    }
  };

  return (
    <form css={styles.panel} onSubmit={upload}>
      <div css={styles.heading}>{'Batch Upload'}</div>
      <div css={styles.description}>
        {`Creates properties from a CSV with the header row ${ORG_PROPERTIES_CSV_REQUIRED_COLUMNS.join(', ')}, and optionally ${
          ORG_PROPERTIES_CSV_OPTIONAL_COLUMNS.join(', ')
        }. Each row's property goes in the org its orgId names. customId is stored as the customer property ID; market is an active market name in that org, or empty. Properties that already exist are left unchanged.`}
      </div>
      <div css={styles.actions}>
        <CommonActionButton onClick={() => downloadBlob(new Blob([getSampleCsv(currentOrg?.id ?? '')], { type: 'text/csv' }), 'batch-upload-sample.csv')}>
          {'Download sample CSV'}
        </CommonActionButton>
      </div>
      <input type='file' accept='.csv,text/csv' aria-label='Properties CSV' onChange={event => setFile(event.target.files?.[0])} />
      <div css={styles.actions}>
        <CommonActionButton type='submit' disabled={!file || batchUpload.isPending}>{batchUpload.isPending ? 'Uploading...' : 'Upload'}</CommonActionButton>
      </div>
      {errorMessage && <div css={styles.error}>{errorMessage}</div>}
      {response && <BatchUploadSummary response={response} styles={styles} />}
    </form>
  );
};
